import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type CentralContractLifecycleState =
  | "draft"
  | "pending_approval"
  | "issued"
  | "partially_signed"
  | "signed"
  | "declined"
  | "expired"
  | "voided"
  | "replaced"
  | "amended";

export type CentralContractIssuePolicy =
  | { decisionId: "DEC-01"; state: "default_off" }
  | {
      approvedAt: string;
      approvedByRole: string;
      decisionId: "DEC-01";
      evidenceRef: string;
      state: "professionally_approved";
    };

export type CentralContractLifecycleRecord = {
  contractId: string;
  currentRevisionId: string;
  currentRevisionNumber: number;
  document: {
    plainText: string;
    plainTextChecksumSha256: string;
    quoteId: string;
    renderedChecksumSha256: string;
    renderedHtml: string;
    sourceChecksumSha256: string;
    templateChecksumSha256: string;
    templateId: string;
    templateVersion: number;
  };
  expiresAt: string | null;
  issuedAt: string | null;
  lineage: {
    parentContractId: string | null;
    relation: "amendment" | "replacement" | null;
    rootContractId: string;
  };
  reminderEligibility: "eligible" | "not_started" | "stop_requested";
  replacedByContractId: string | null;
  signers: Array<{
    displayName: string;
    role: "authorized_representative" | "co_purchaser" | "purchaser";
  }>;
  signedAt: string | null;
  state: CentralContractLifecycleState;
  terminalReasonCode: string | null;
  updatedAt: string;
  version: number;
};

export type CentralContractLifecycleResult = {
  contract: CentralContractLifecycleRecord | null;
  issuePolicy: CentralContractIssuePolicy;
  latestTransition?: {
    action: string;
    occurredAt: string;
    reasonCode: string;
    transitionId: string;
  };
};

const functions = getFunctions(firebaseApp, "us-east1");
let commandSequence = 0;

export function newContractLifecycleIdempotencyKey(
  caseId: string,
  action: string,
) {
  commandSequence += 1;
  return `contract_${action}_${caseId}_${Date.now().toString(36)}_${commandSequence.toString(36)}`;
}

export async function getCentralContractLifecycle(
  caseId: string,
  contractId?: string,
) {
  const callable = httpsCallable<
    { caseId: string; contractId?: string },
    CentralContractLifecycleResult
  >(functions, "getCentralContractLifecycle");
  return (await callable({ caseId, contractId })).data;
}

export async function transitionCentralContractLifecycle(input: {
  action:
    | "abandon"
    | "decline"
    | "expire"
    | "issue"
    | "return_to_draft"
    | "submit_for_approval"
    | "void";
  caseId: string;
  contractId: string;
  expectedVersion: number;
  expiresAt?: string;
  idempotencyKey: string;
  reasonCode?: string;
  reasonNote?: string;
}) {
  const callable = httpsCallable<typeof input, CentralContractLifecycleRecord>(
    functions,
    "transitionCentralContractLifecycle",
  );
  return (await callable(input)).data;
}

export async function createCentralContractLifecycleDraft(input: {
  caseId: string;
  delivery: {
    acknowledgedAt: string | null;
    acknowledgedByActorId: string | null;
    method: "electronic" | "human_assisted" | "paper";
  };
  expectedCurrentVersion?: number;
  idempotencyKey: string;
  parties: {
    payerNames: string[];
    purchaserName: string;
    signers: Array<{
      displayName: string;
      role: "authorized_representative" | "co_purchaser" | "purchaser";
    }>;
  };
  quoteId: string;
  templateId: string;
  templateVersion: number;
}) {
  const callable = httpsCallable<typeof input, CentralContractLifecycleRecord>(
    functions,
    "createCentralContractLifecycleDraft",
  );
  return (await callable(input)).data;
}

export async function reviseCentralContractLifecycle(input: {
  caseId: string;
  contractId: string;
  delivery: {
    acknowledgedAt: string | null;
    acknowledgedByActorId: string | null;
    method: "electronic" | "human_assisted" | "paper";
  };
  expectedVersion: number;
  idempotencyKey: string;
  materialChangeSummary: string;
  parties: {
    payerNames: string[];
    purchaserName: string;
    signers: Array<{
      displayName: string;
      role: "authorized_representative" | "co_purchaser" | "purchaser";
    }>;
  };
  quoteId: string;
  templateId: string;
  templateVersion: number;
}) {
  const callable = httpsCallable<typeof input, CentralContractLifecycleRecord>(
    functions,
    "reviseCentralContractLifecycle",
  );
  return (await callable(input)).data;
}

export async function createCentralContractLifecycleSuccessor(input: {
  caseId: string;
  delivery: {
    acknowledgedAt: string | null;
    acknowledgedByActorId: string | null;
    method: "electronic" | "human_assisted" | "paper";
  };
  expectedVersion: number;
  idempotencyKey: string;
  materialChangeSummary: string;
  parentContractId: string;
  parties: {
    payerNames: string[];
    purchaserName: string;
    signers: Array<{
      displayName: string;
      role: "authorized_representative" | "co_purchaser" | "purchaser";
    }>;
  };
  quoteId: string;
  successorKind: "amendment" | "replacement";
  templateId: string;
  templateVersion: number;
}) {
  const callable = httpsCallable<
    typeof input,
    {
      child: CentralContractLifecycleRecord;
      parent: CentralContractLifecycleRecord;
    }
  >(functions, "createCentralContractLifecycleSuccessor");
  return (await callable(input)).data;
}

export function openAccessibleContractDocument(
  record: CentralContractLifecycleRecord,
) {
  if (typeof window === "undefined") {
    throw new Error(
      "The accessible contract statement can be opened from the Central web workspace.",
    );
  }
  const blob = new Blob([record.document.renderedHtml], {
    type: "text/html;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    URL.revokeObjectURL(url);
    throw new Error(
      "The browser blocked the accessible statement window. Allow pop-ups and try again.",
    );
  }
  opened.opener = null;
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function downloadAccessibleContractPlainText(
  record: CentralContractLifecycleRecord,
) {
  if (typeof document === "undefined") {
    throw new Error(
      "The plain-text contract statement can be downloaded from the Central web workspace.",
    );
  }
  const blob = new Blob([record.document.plainText], {
    type: "text/plain;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${record.contractId}-revision-${record.currentRevisionNumber}.txt`;
  anchor.rel = "noopener";
  anchor.click();
  URL.revokeObjectURL(url);
}

export function getContractLifecycleErrorMessage(error: unknown) {
  const code =
    typeof error === "object" && error && "code" in error
      ? String(error.code)
      : "";
  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message).replace(/^FirebaseError:\s*/i, "")
      : "";
  if (code.includes("aborted")) {
    return "The contract changed. Refresh its immutable revision before trying again.";
  }
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "A director with recent authentication and access to this case is required.";
  }
  if (
    code.includes("failed-precondition") ||
    code.includes("invalid-argument")
  ) {
    return message ||
      "The contract action is not available from its current immutable state.";
  }
  if (code.includes("not-found")) {
    return "The governed contract or its immutable revision is unavailable.";
  }
  return "Central could not complete the governed contract action. Retry with the same request if the result is uncertain.";
}
